import React from 'react';
import { format } from 'date-fns';
import { Star, CheckCircle2, Loader2, ExternalLink } from 'lucide-react';
import { FormattedText } from '../../../lib/formatContent';
import { PostImage } from './PostImage';

interface PostHistoryProps {
  isLoading: boolean;
  history: any[];
  onSetDefault: (post: any) => void;
}

export const PostHistory: React.FC<PostHistoryProps> = ({ isLoading, history, onSetDefault }) => {
  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  // Sort posts to show in the following order:
  // 1. Published posts first (newest first)
  // 2. Default ready posts second (these are the ones marked as default)
  // 3. Ready posts third (these are the ones not marked as default)
  // 4. Then all other posts by creation date (newest first)
  // Skip posts marked as 'skipped'
  const sortedHistory = [...history]
    .filter(post => post.status !== 'skipped')
    .sort((a, b) => {
      // Published posts should be first
      const aIsPublished = a.status === 'published' || a.platform_post_id || a.platform_post_url || a.metadata?.platform_post_id || a.metadata?.platform_post_url;
      const bIsPublished = b.status === 'published' || b.platform_post_id || b.platform_post_url || b.metadata?.platform_post_id || b.metadata?.platform_post_url;
      
      if (aIsPublished && !bIsPublished) return -1;
      if (!aIsPublished && bIsPublished) return 1;
      
      // Default ready posts should be second
      if ((a.table === 'ready_posts' && a.is_default) && !(b.table === 'ready_posts' && b.is_default)) return -1;
      if (!(a.table === 'ready_posts' && a.is_default) && (b.table === 'ready_posts' && b.is_default)) return 1;
      
      // Ready posts should be third
      if ((a.status === 'ready' || a.table === 'ready_posts') && 
          (b.status !== 'ready' && b.table !== 'ready_posts')) return -1;
      if ((a.status !== 'ready' && a.table !== 'ready_posts') && 
          (b.status === 'ready' || b.table === 'ready_posts')) return 1;
      
      // If both are published, ready, or default, sort by creation date (newest first)
      if ((a.status === b.status) || 
         (a.table === 'ready_posts' && b.table === 'ready_posts') ||
         ((a.status === 'ready' || a.table === 'ready_posts') && 
          (b.status === 'ready' || b.table === 'ready_posts'))) {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
      
      // Finally sort remaining posts by creation date (newest first)
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

  return (
    <div className="space-y-3">
      {sortedHistory.map((historyPost) => {
        // Get the content and image from webhook response if available
        // Handle array response format
        let webhookResponse = historyPost.metadata?.webhook_response;
        if (Array.isArray(webhookResponse)) {
          webhookResponse = webhookResponse[0];
        }
        
        const displayContent = webhookResponse?.data?.content || historyPost.content;
        const displayImage = webhookResponse?.data?.image_url || historyPost.image_url;
        
        // Create a complete post object for publication
        const completePost = {
          ...historyPost,
          content: displayContent,
          image_url: displayImage,
          // Preserve webhook response and metadata
          metadata: {
            ...historyPost.metadata,
            webhook_response: webhookResponse ? {
              data: {
                ...webhookResponse.data,
                content: displayContent,
                image_url: displayImage
              }
            } : null
          }
        };
        
        // Determine if this post is the default one
        const isDefault = historyPost.table === 'ready_posts' && historyPost.is_default;
        
        // Determine if this post is published
        const isPublished = historyPost.status === 'published' || 
                           historyPost.platform_post_id || 
                           historyPost.platform_post_url ||
                           historyPost.metadata?.platform_post_id ||
                           historyPost.metadata?.platform_post_url;
        
        return (
          <div
            key={historyPost.id}
            className={`p-4 border rounded-lg ${
              isPublished
                ? 'bg-green-50 border-green-200 shadow-sm'
                : isDefault
                ? 'bg-purple-50 border-purple-200 shadow-sm'
                : historyPost.status === 'ready' || historyPost.table === 'ready_posts'
                ? 'bg-purple-50 border-purple-200'
                : historyPost.status === 'published'
                ? 'bg-green-50 border-green-200'
                : 'bg-gray-50 border-gray-200'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 flex-wrap">
                {isPublished && (
                  <CheckCircle2 className="w-4 h-4 text-green-600 fill-current" />
                )}
                {isDefault && !isPublished && (
                  <Star className="w-4 h-4 text-purple-600 fill-current" />
                )}
                {!isDefault && !isPublished && (historyPost.status === 'ready' || historyPost.table === 'ready_posts') && (
                  <Star className="w-4 h-4 text-purple-600" />
                )}
                <span className="text-sm text-gray-500">
                  {format(new Date(historyPost.created_at), 'PPP p')}
                </span>
                {historyPost.execution_id && (
                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-mono" title={`Execution ID: ${historyPost.execution_id}`}>
                    exec-{historyPost.execution_id.slice(0, 8)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {(historyPost.platform_post_url || historyPost.metadata?.platform_post_url) && (
                  <a
                    href={historyPost.platform_post_url || historyPost.metadata?.platform_post_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 text-gray-400 hover:text-blue-600 rounded-full hover:bg-blue-50"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                {!isPublished && (
                  <button
                    onClick={() => onSetDefault(completePost)}
                    className={`text-sm px-3 py-1 rounded-full ${
                      isDefault
                        ? 'bg-purple-100 text-purple-700 font-medium'
                        : 'bg-gray-100 text-gray-700 hover:bg-purple-100 hover:text-purple-700'
                    }`}
                  >
                    {isDefault ? 'Default' : 'Use for publication'}
                  </button>
                )}
              </div>
            </div>
            <div className="prose max-w-none">
              <FormattedText content={displayContent} className="text-gray-700" />
            </div>
            {displayImage && (
              <div className="mt-2">
                <PostImage imageUrl={displayImage} />
              </div>
            )}
            {(historyPost.platform_post_created_at || historyPost.metadata?.platform_post_created_at) && (
              <div className="mt-2 text-sm text-gray-500">
                Published: {format(new Date(historyPost.platform_post_created_at || historyPost.metadata?.platform_post_created_at), 'PPP p')}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};